import { useQueryClient } from '@tanstack/react-query';
import { format, parseISO } from 'date-fns';
import { Image } from 'expo-image';
import { useEffect, useMemo, useState } from 'react';
import { ScrollView, View } from 'react-native';
import Modal from 'react-native-modal';
import { createStyleSheet, useStyles } from 'react-native-unistyles';

import { getSlackWorkspaceUrl } from '@/api/partners/integrations/slack/get-slack-workspace-url';
import type {
  SlackIntegration,
  SlackNotificationPrefKey,
  SlackNotificationPrefs,
} from '@/api/partners/integrations/slack/types';
import { useDisconnectSlackIntegration } from '@/api/partners/integrations/slack/use-disconnect-slack-integration';
import { useSlackChannels } from '@/api/partners/integrations/slack/use-slack-channels';
import { useSlackIntegration } from '@/api/partners/integrations/slack/use-slack-integration';
import { useUpdateSlackIntegration } from '@/api/partners/integrations/slack/use-update-slack-integration';
import {
  NavigationButton,
  NavigationButtonTypes,
} from '@/components/navigation-button';
import { Button, Text } from '@/ui';
import Alert, { AlertType } from '@/ui/alert';
import { Chip, ChipType } from '@/ui/chip';
import Dropdown from '@/ui/dropdown';
import { Loader } from '@/ui/loader';
import Toggle from '@/ui/toggle';

import { SLACK_ICON_URL } from '../constants';

const NOTIFICATION_OPTIONS: {
  key: SlackNotificationPrefKey;
  title: string;
  description: string;
}[] = [
  {
    key: 'newReservation',
    title: 'New Reservation',
    description: 'When a customer books',
  },
  {
    key: 'cancellation',
    title: 'Cancellation',
    description: 'When a customer cancels a trip',
  },
  {
    key: 'reservationStatus',
    title: 'Reservation Status',
    description: 'Trip verification and confirmation updates',
  },
  {
    key: 'newSubscription',
    title: 'New Subscription',
    description: 'When a customer subscribes',
  },
];

type Props = {
  visible: boolean;
  integration?: SlackIntegration;
  onClose: () => void;
};

export function SlackSettingsModal({ visible, integration, onClose }: Props) {
  const { styles } = useStyles(stylesheet);
  const queryClient = useQueryClient();
  const [channelError, setChannelError] = useState('');
  const [actionError, setActionError] = useState('');
  const [notificationPrefs, setNotificationPrefs] =
    useState<SlackNotificationPrefs>({
      newReservation: false,
      cancellation: false,
      reservationStatus: false,
      newSubscription: false,
    });
  const [updatingPrefKey, setUpdatingPrefKey] =
    useState<SlackNotificationPrefKey | null>(null);

  const { mutateAsync: updateSlackIntegration, isPending: isUpdatingChannel } =
    useUpdateSlackIntegration();
  const { mutateAsync: disconnectSlack, isPending: isDisconnecting } =
    useDisconnectSlackIntegration();

  const shouldFetchChannels = visible && integration?.status !== 'disconnected';

  const { data: channels = [], isPending: isLoadingChannels } =
    useSlackChannels({
      variables: { filter: 'all' },
      enabled: shouldFetchChannels,
    });

  useEffect(() => {
    if (!integration?.notificationPrefs) {
      return;
    }

    setNotificationPrefs(integration.notificationPrefs);
  }, [integration]);

  const channelOptions = useMemo(
    () =>
      channels.map((channel) => ({
        id: channel.id,
        value: channel.id,
        title: `# ${channel.name}`,
        caption: channel.isPrivate
          ? channel.isMember
            ? 'Private'
            : 'Private — invite the bot'
          : 'Public',
      })),
    [channels]
  );

  const connectedAtLabel = useMemo(() => {
    if (!integration?.connectedAt || !integration.connectedBy?.name) {
      return null;
    }

    return `Connected by ${integration.connectedBy.name} on ${format(
      parseISO(integration.connectedAt),
      'MMMM d, yyyy'
    )}`;
  }, [integration]);

  const workspaceUrl = getSlackWorkspaceUrl(integration?.workspace);

  const invalidateSlackIntegration = async () => {
    await queryClient.invalidateQueries({
      queryKey: useSlackIntegration.getKey(),
    });
  };

  const handleClose = () => {
    setChannelError('');
    setActionError('');
    onClose();
  };

  const handleChannelSelect = async (selected: { value: string }[]) => {
    const channelId = selected[0]?.value;
    if (!channelId) {
      return;
    }

    setChannelError('');

    try {
      await updateSlackIntegration({ channelId });
      await invalidateSlackIntegration();
    } catch (error) {
      setChannelError(
        error instanceof Error ? error.message : 'Could not save channel'
      );
    }
  };

  const handleTogglePref = async (key: SlackNotificationPrefKey) => {
    if (!integration?.channel) {
      return;
    }

    const nextValue = !notificationPrefs[key];
    const previousPrefs = notificationPrefs;

    setNotificationPrefs((current) => ({ ...current, [key]: nextValue }));
    setUpdatingPrefKey(key);
    setActionError('');

    try {
      await updateSlackIntegration({
        notificationPrefs: { [key]: nextValue },
      });
      await invalidateSlackIntegration();
    } catch (error) {
      setNotificationPrefs(previousPrefs);
      setActionError(
        error instanceof Error ? error.message : 'Could not update notification'
      );
    } finally {
      setUpdatingPrefKey(null);
    }
  };

  const handleDisconnect = async () => {
    setActionError('');

    try {
      await disconnectSlack();
      await invalidateSlackIntegration();
      handleClose();
    } catch (error) {
      setActionError(
        error instanceof Error ? error.message : 'Could not disconnect Slack'
      );
    }
  };

  if (!integration) {
    if (!visible) {
      return null;
    }

    return (
      <Modal
        animationIn="slideInUp"
        animationInTiming={300}
        animationOut="slideOutDown"
        animationOutTiming={300}
        isVisible={visible}
        hasBackdrop
        style={styles.container}
        onBackdropPress={handleClose}
      >
        <View style={[styles.containerView, styles.loadingView]}>
          <Loader style={styles.channelLoader} />
        </View>
      </Modal>
    );
  }

  return (
    <Modal
      animationIn="slideInUp"
      animationInTiming={300}
      animationOut="slideOutDown"
      animationOutTiming={300}
      isVisible={visible}
      hasBackdrop
      style={styles.container}
      onBackdropPress={handleClose}
    >
      <View style={styles.containerView}>
        <View style={styles.header}>
          <NavigationButton
            type={NavigationButtonTypes.cancel}
            onPress={handleClose}
          />
        </View>

        <ScrollView
          contentContainerStyle={styles.content}
          keyboardShouldPersistTaps="handled"
        >
          <View style={styles.workspaceRow}>
            <View style={styles.workspaceInfo}>
              <Image
                source={{ uri: SLACK_ICON_URL }}
                style={styles.icon}
                contentFit="cover"
              />

              {workspaceUrl ? (
                <Text type="title2" style={styles.workspaceUrl}>
                  {workspaceUrl}
                </Text>
              ) : null}
            </View>

            <Chip type={ChipType.SUCCESS} text="Connected" />
          </View>

          {integration.status === 'action_needed' ? (
            <Alert
              type={AlertType.WARNING}
              title="Channel unavailable"
              body="The bot can't post to the selected channel. Invite the bot or choose another channel."
            />
          ) : null}

          <View style={styles.section}>
            <Text type="title2" style={styles.sectionTitle}>
              Post Updates To
            </Text>

            {isLoadingChannels ? (
              <Loader style={styles.channelLoader} />
            ) : (
              <Dropdown
                placeHolderText="Select a channel"
                options={channelOptions}
                values={integration.channel ? [integration.channel.id] : []}
                onSelect={handleChannelSelect}
                error={channelError}
              />
            )}

            {!integration.channel ? (
              <Text type="caption1" style={styles.secondaryText}>
                {workspaceUrl
                  ? `${workspaceUrl} will post to this channel. Make sure the bot is a member of private channels.`
                  : 'Updates will post to this channel. Make sure the bot is a member of private channels.'}
              </Text>
            ) : null}
          </View>

          {integration?.channel ? (
            <View style={styles.section}>
              <Text type="title2" style={styles.sectionTitle}>
                Notify Me When…
              </Text>

              {NOTIFICATION_OPTIONS.map((option) => (
                <View key={option.key} style={styles.toggleRow}>
                  <View style={styles.toggleCopy}>
                    <Text type="title2">{option.title}</Text>
                    <Text type="bodyRegular" style={styles.secondaryText}>
                      {option.description}
                    </Text>
                  </View>

                  <Toggle
                    enabled={notificationPrefs[option.key]}
                    onToggle={() => handleTogglePref(option.key)}
                  />
                </View>
              ))}
            </View>
          ) : null}

          {updatingPrefKey || isUpdatingChannel ? (
            <Text type="caption1" style={styles.secondaryText}>
              Saving…
            </Text>
          ) : null}

          {actionError ? (
            <Text type="caption1" style={styles.errorText}>
              {actionError}
            </Text>
          ) : null}

          {connectedAtLabel ? (
            <Text type="caption1" style={styles.secondaryText}>
              {connectedAtLabel}
            </Text>
          ) : null}

          <View style={styles.footer}>
            <Button
              text="Disconnect"
              type="secondary"
              onPress={handleDisconnect}
              loading={isDisconnecting}
              disabled={isDisconnecting}
              style={styles.footerButton}
              textStyle={styles.disconnectText}
            />
          </View>
        </ScrollView>
      </View>
    </Modal>
  );
}

const stylesheet = createStyleSheet((theme) => ({
  container: {
    margin: 0,
    marginTop: 48,
    paddingBottom: {
      xs: 16,
      md: 0,
    },
    alignSelf: 'center',
    maxWidth: {
      xs: '100%',
      sm: 640,
    },
    width: {
      xs: '100%',
    },
  },
  containerView: {
    backgroundColor: theme.colors.background_2,
    borderRadius: 16,
    borderBottomLeftRadius: {
      xs: 0,
      md: 16,
    },
    borderBottomRightRadius: {
      xs: 0,
      md: 16,
    },
    maxHeight: '90%',
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    paddingTop: 16,
    paddingRight: 16,
  },
  content: {
    paddingHorizontal: 16,
    paddingTop: 24,
    paddingBottom: 16,
    gap: 24,
  },
  workspaceRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
  },
  workspaceInfo: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    minWidth: 0,
  },
  workspaceUrl: {
    flex: 1,
  },
  icon: {
    width: 48,
    height: 48,
    borderRadius: 8,
  },
  section: {
    gap: 16,
  },
  sectionTitle: {
    color: theme.colors.text.secondary,
  },
  secondaryText: {
    color: theme.colors.text.secondary,
  },
  errorText: {
    color: theme.colors.error,
  },
  channelLoader: {
    height: 80,
    width: 80,
    alignSelf: 'center',
  },
  loadingView: {
    minHeight: 240,
    alignItems: 'center',
    justifyContent: 'center',
  },
  toggleRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 16,
  },
  toggleCopy: {
    flex: 1,
    gap: 4,
  },
  footer: {
    borderTopWidth: 1,
    borderTopColor: theme.colors.card.default.borderColor,
    paddingTop: 16,
  },
  footerButton: {
    minWidth: 150,
  },
  disconnectText: {
    color: theme.colors.error,
  },
}));
